// İSG uzmanı portalı — DÖF (Düzeltici/Önleyici Faaliyet) oluştur/düzenle formu (Modal + RHF +
// Zod). api-contract §19.4: `POST .../items/{itemUuid}/actions` (create) / `PUT .../actions/
// {actionUuid}` (update). Alanlar: açıklama (zorunlu) + sorumlu kullanıcı (Select; aynı firma) +
// termin tarihi + durum (Open/InProgress/Done/Cancelled). `completedAt` sunucu yönetir (Done'a
// geçişte set) — istemci göndermez.
//
// Yalnız düzenlenebilir değerlendirmede açılır (BE `409 RISK_ASSESSMENT_NOT_EDITABLE` UI'da önlenir).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  type RiskActionDto,
  type RiskActionStatus,
  Select,
  type SelectOption,
  Spinner,
  resolveApiErrorMessage,
  riskApi,
  usersApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { riskKeys } from '../../lib/riskKeys';
import { RISK_ACTION_STATUSES } from './riskEnums';

const OPTIONS_PAGE_SIZE = 200;

interface RiskActionFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentUuid: string;
  itemUuid: string;
  /** Verilirse düzenleme; yoksa oluşturma. */
  action?: RiskActionDto | null;
}

/** UTC ISO → `yyyy-MM-dd` (date input; §4 sunum). */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `yyyy-MM-dd` (yerel gün başlangıcı) → UTC ISO wire (§4); boş → null. */
function dateToIso(dateStr: string): string | null {
  if (!dateStr) {
    return null;
  }
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function RiskActionFormModal({
  open,
  onOpenChange,
  assessmentUuid,
  itemUuid,
  action,
}: RiskActionFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(action);

  // Sorumlu kullanıcı seçici (§12) — aktif kullanıcıları çeker; FE `isActive` ile süzer.
  const usersQuery = useQuery({
    queryKey: ['risk', 'options', 'users'],
    queryFn: () => usersApi.listUsers({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });

  const userOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('risk.action.responsibleNone') },
      ...(usersQuery.data?.items ?? [])
        .filter((u) => u.isActive)
        .map((u) => ({ value: u.uuid, label: `${u.firstName} ${u.lastName}`.trim() })),
    ],
    [t, usersQuery.data],
  );

  const statusOptions: SelectOption[] = useMemo(
    () => RISK_ACTION_STATUSES.map((s) => ({ value: s, label: t(`risk.action.status.${s}`) })),
    [t],
  );

  const schema = useMemo(
    () =>
      z.object({
        description: z.string().trim().min(1, t('risk.action.validation.descriptionRequired')),
        responsibleUserId: z.string(),
        dueDate: z.string(),
        status: z.enum(['Open', 'InProgress', 'Done', 'Cancelled']),
        note: z.string(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      description: action?.description ?? '',
      responsibleUserId: action?.responsibleUserId ?? '',
      dueDate: toDateInput(action?.dueDate),
      status: (action?.status ?? 'Open') as RiskActionStatus,
      note: action?.note ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const body = {
        description: values.description.trim(),
        responsibleUserId: values.responsibleUserId.length > 0 ? values.responsibleUserId : null,
        dueDate: dateToIso(values.dueDate),
        status: values.status,
        note: values.note.trim().length > 0 ? values.note.trim() : null,
      };
      return action
        ? riskApi.updateRiskAction(assessmentUuid, itemUuid, action.uuid, body)
        : riskApi.createRiskAction(assessmentUuid, itemUuid, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessmentUuid) });
      reset();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('risk.action.edit.title') : t('risk.action.add.title')}
      description={t('risk.action.form.description')}
      size="md"
      closeLabel={t('risk.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('risk.common.cancel')}
          </Button>
          <Button type="submit" form="risk-action-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('risk.common.save')} />
            ) : isEdit ? (
              t('risk.common.save')
            ) : (
              t('risk.common.add')
            )}
          </Button>
        </>
      }
    >
      <form
        id="risk-action-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <Field
          htmlFor="action-description"
          label={t('risk.action.field.description')}
          error={formState.errors.description?.message}
          required
        >
          <Input
            id="action-description"
            invalid={Boolean(formState.errors.description)}
            {...register('description')}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="action-responsible" label={t('risk.action.field.responsible')}>
            <Select
              id="action-responsible"
              disabled={usersQuery.isPending}
              options={userOptions}
              {...register('responsibleUserId')}
            />
          </Field>
          <Field htmlFor="action-due" label={t('risk.action.field.dueDate')}>
            <Input id="action-due" type="date" {...register('dueDate')} />
          </Field>
          <Field htmlFor="action-status" label={t('risk.action.field.status')}>
            <Select id="action-status" options={statusOptions} {...register('status')} />
          </Field>
          <Field htmlFor="action-note" label={t('risk.action.field.note')}>
            <Input id="action-note" {...register('note')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
